// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { IInput, IModule, IOutput } from "shared/types/parameter";
import { IState } from "states/states";
import { IStateDataOutput } from "states/datastates";
import { ITableSort } from "states/guistates";
import { makeSortHeader } from "guiutils";
import { isSet } from "shared/utils/utils";

import Input from "view/comp/Input";
import Output from "view/comp/Output";
import ModalInput from "view/modal/ModalInput";
import ModalOutput from "view/modal/ModalOutput";
import SiteBar, { ISiteBarSearchFieldProps } from "view/comp/SiteBar";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// IoList is both the IOs page (SiteBar + full grouped table + edit modals)
// and, via isDashboard/onlyType, the input/output dashboard widgets: it groups
// inputs+outputs by category if any IO uses one, otherwise by type, and
// renders everything as a single aligned table.

type IoType = "input" | "output";

function getIoListTableName(onlyType?: IoType) {
  return onlyType ? `t_ios_${onlyType}` : "t_ios";
}

interface IIoEntry {
  kind: IoType;
  index: number;
  name: string;
  module: number;
  category: string | undefined;
}

interface IIoGroup {
  key: string;
  title: React.ReactNode;
  kind: IoType | undefined; // undefined = mixed input+output group (a category)
  items: IIoEntry[];
}

interface IIoListExtProps {
  isDashboard?: boolean;
  onlyType?: IoType; // restrict to a single type, e.g. for dashboard widgets
  isInputEditable?: boolean; // defaults to the current user's access rights
  isOutputEditable?: boolean; // defaults to the current user's access rights
}

interface IIoListProps {
  inputs: IInput[];
  outputs: IOutput[];
  inputValues: number[];
  outputValues: IStateDataOutput[];
  modules: IModule[] | undefined;
  showModules: boolean;
  showId: boolean;
  filterText?: string;
  tableSort: ITableSort | undefined;
  isInputEditable: boolean;
  isOutputEditable: boolean;
}

const mapStateToProps = (state: IState, ownProps: IIoListExtProps) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const showModules =
    !ownProps.isDashboard &&
    Boolean(curUser?.accessRights.controllerAdmin) &&
    Boolean(state.para.parameter?.module.length);
  const hasInputWriteAccess =
    (!IS_CLOUD && state.site.user?.secId === "admin") ||
    (IS_CLOUD && (curUser?.accessRights.setVirtualInputs ?? false));
  const hasOutputWriteAccess =
    (!IS_CLOUD && state.site.user?.secId === "admin") ||
    (IS_CLOUD && (curUser?.accessRights.switchOutputs ?? false));

  const props: IIoListProps = {
    inputs: state.para.parameter?.input ?? [],
    outputs: state.para.parameter?.output ?? [],
    inputValues: state.data.input,
    outputValues: state.data.output,
    modules: state.para.parameter?.module,
    showModules,
    showId: !ownProps.isDashboard && Boolean(curUser?.accessRights.setVirtualParameters),
    filterText: state.gui.searchFilterText,
    tableSort: state.gui.tableSort[getIoListTableName(ownProps.onlyType)],
    isInputEditable: ownProps.isInputEditable ?? hasInputWriteAccess,
    isOutputEditable: ownProps.isOutputEditable ?? hasOutputWriteAccess,
  };
  return props;
};

interface IIoListDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoListDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  placeholderSearchField: {
    id: "Search for in-/outputs...",
  },
});

class IoListComponent extends React.Component<IIoListProps & IIoListExtProps & IIoListDispatch> {
  private getSortKey(entry: IIoEntry, tableSort: ITableSort | undefined) {
    const sortCol = tableSort?.column as "name" | "module" | "value" | undefined;
    if (sortCol === "module") {
      return this.props.modules?.find((curItem) => curItem.id === entry.module)?.name;
    }
    if (sortCol === "value") {
      if (entry.kind === "input") {
        const input = this.props.inputs[entry.index];
        // derive some kind of hash value from unit string, just to support numeric sorting
        // in old parameter sets, the unit is a number
        const unit = input.unit;
        const unitValue =
          typeof unit === "string" ?
            unit.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
          : typeof unit === "number" ? unit
          : 0;
        return input.type * 1000000 + unitValue * 1000 + this.props.inputValues[entry.index];
      }
      const output = this.props.outputs[entry.index];
      return output.mode * 100 + (this.props.outputValues[entry.index]?.logical ?? 0);
    }
    return entry.name;
  }

  private compareSortKeys(keyA: string | number | undefined, keyB: string | number | undefined) {
    if (isSet(keyA) && isSet(keyB))
      return (
        keyA > keyB ? 1
        : keyA < keyB ? -1
        : 0
      );
    if (isSet(keyA)) return 1;
    if (isSet(keyB)) return -1;
    return 0;
  }

  private collectEntries(): IIoEntry[] {
    const filterTextLowerCase = (this.props.filterText ?? "").toLocaleLowerCase();
    const entries: IIoEntry[] = [];

    if (this.props.onlyType !== "output") {
      this.props.inputs.forEach((curItem, curIndex) => {
        if (curItem.webActive && curItem.name.toLocaleLowerCase().includes(filterTextLowerCase)) {
          entries.push({
            kind: "input",
            index: curIndex,
            name: curItem.name,
            module: curItem.module,
            category: curItem.categories?.[0],
          });
        }
      });
    }
    if (this.props.onlyType !== "input") {
      this.props.outputs.forEach((curItem, curIndex) => {
        if (curItem.webActive && curItem.name.toLocaleLowerCase().includes(filterTextLowerCase)) {
          entries.push({
            kind: "output",
            index: curIndex,
            name: curItem.name,
            module: curItem.module,
            category: curItem.categories?.[0],
          });
        }
      });
    }
    return entries;
  }

  // group by category (with a legacy type-based tail for uncategorized items);
  // falls back to a pure type-based split when no category is in use at all
  private buildGroups(): IIoGroup[] {
    const entries = this.collectEntries();

    if (this.props.isDashboard) {
      return [{ key: "flat", title: undefined, kind: this.props.onlyType, items: entries }];
    }

    const categorized = entries.filter((curItem) => curItem.category);
    if (categorized.length === 0) {
      return this.buildTypeGroups(entries);
    }

    const categoryNames = Array.from(new Set(categorized.map((curItem) => curItem.category!))).sort(
      (a, b) => a.localeCompare(b),
    );
    const groups: IIoGroup[] = categoryNames.map((category) => ({
      key: category,
      title: category,
      kind: undefined,
      items: categorized.filter((curItem) => curItem.category === category),
    }));

    const uncategorized = entries.filter((curItem) => !curItem.category);
    const uncategorizedInputs = uncategorized.filter((curItem) => curItem.kind === "input");
    const uncategorizedOutputs = uncategorized.filter((curItem) => curItem.kind === "output");
    if (uncategorizedInputs.length) {
      groups.push({
        key: "inputs",
        title: <FormattedMessage id="Inputs" />,
        kind: "input",
        items: uncategorizedInputs,
      });
    }
    if (uncategorizedOutputs.length) {
      groups.push({
        key: "outputs",
        title: <FormattedMessage id="Outputs" />,
        kind: "output",
        items: uncategorizedOutputs,
      });
    }
    return groups;
  }

  private buildTypeGroups(entries: IIoEntry[]): IIoGroup[] {
    if (this.props.onlyType) {
      return [
        {
          key: this.props.onlyType,
          title:
            this.props.onlyType === "input" ?
              <FormattedMessage id="Inputs" />
            : <FormattedMessage id="Outputs" />,
          kind: this.props.onlyType,
          items: entries,
        },
      ];
    }

    const groups: IIoGroup[] = [];
    const inputs = entries.filter((curItem) => curItem.kind === "input");
    const outputs = entries.filter((curItem) => curItem.kind === "output");
    // hide a type's section entirely only if the site has neither type configured
    const siteHasInputs = this.props.inputs.some((curItem) => curItem.webActive);
    const siteHasOutputs = this.props.outputs.some((curItem) => curItem.webActive);

    if (siteHasInputs || !siteHasOutputs) {
      groups.push({
        key: "inputs",
        title: <FormattedMessage id="Inputs" />,
        kind: "input",
        items: inputs,
      });
    }
    if (siteHasOutputs || !siteHasInputs) {
      groups.push({
        key: "outputs",
        title: <FormattedMessage id="Outputs" />,
        kind: "output",
        items: outputs,
      });
    }
    return groups;
  }

  private renderRow(entry: IIoEntry) {
    return entry.kind === "input" ?
        <Input
          key={`i${entry.index}`}
          index={entry.index}
          isEditable={this.props.isInputEditable}
          showModule={this.props.showModules}
          isDashboard={this.props.isDashboard}
        />
      : <Output
          key={`o${entry.index}`}
          index={entry.index}
          isEditable={this.props.isOutputEditable}
          showModule={this.props.showModules}
          isDashboard={this.props.isDashboard}
        />;
  }

  private renderGroup(group: IIoGroup, numCols: number) {
    const tableName = getIoListTableName(this.props.onlyType);
    const tableSort = this.props.tableSort;

    const sortedItems = [...group.items].sort((a, b) => {
      const primary = this.compareSortKeys(
        this.getSortKey(a, tableSort),
        this.getSortKey(b, tableSort),
      );
      const directedPrimary = tableSort?.isSortAscending !== false ? primary : -primary;
      if (directedPrimary !== 0) return directedPrimary;
      // name is always the tie-break, even when the primary sort is on another column
      if (!tableSort?.column || tableSort.column === "name") return 0;
      return this.compareSortKeys(a.name, b.name);
    });

    return (
      <React.Fragment key={group.key}>
        <thead>
          {group.title !== undefined && (
            <tr className="io-group-header">
              <td colSpan={numCols}>
                <div>
                  <h1>{group.title}</h1>
                </div>
              </td>
            </tr>
          )}
          <tr>
            {this.props.showId && <th className="optional-md">Id</th>}
            {makeSortHeader(
              tableName,
              "name",
              <FormattedMessage id="Name" />,
              tableSort ?? { column: "name", isSortAscending: true },
              this.props.onSort,
            )}
            {this.props.showModules &&
              makeSortHeader(
                tableName,
                "module",
                <FormattedMessage id="Module" />,
                tableSort,
                this.props.onSort,
                ["optional-sm"],
              )}
            {makeSortHeader(
              tableName,
              "value",
              <FormattedMessage id="Value" />,
              tableSort,
              this.props.onSort,
              ["value-col", "right"],
              undefined,
              2,
            )}
            <th className="edit-col"></th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.length ?
            sortedItems.map((entry) => this.renderRow(entry))
          : <tr>
              <td colSpan={numCols} className="alert alert-warning">
                <FormattedMessage
                  id={group.kind === "output" ? "No outputs present." : "No inputs present."}
                />
              </td>
            </tr>
          }
        </tbody>
      </React.Fragment>
    );
  }

  private renderTable() {
    const groups = this.buildGroups();
    // id? + name + module? + icon+value + edit
    const numCols = (this.props.showId ? 1 : 0) + 1 + (this.props.showModules ? 1 : 0) + 2 + 1;
    const wrapperId =
      this.props.onlyType ?
        this.props.onlyType === "input" ?
          "inputs"
        : "outputs"
      : "ioslist";

    return (
      <div id={wrapperId}>
        <table className="datatable fullwidthwhite">
          {groups.map((group) => this.renderGroup(group, numCols))}
        </table>
      </div>
    );
  }

  public render() {
    if (this.props.isDashboard) {
      return this.renderTable();
    }

    const searchFieldProps: ISiteBarSearchFieldProps = {
      placeholder: messages.placeholderSearchField,
    };
    return (
      <>
        <SiteBar showPeriodSelect={false} searchFieldProps={searchFieldProps} />
        <div id="ios">{this.renderTable()}</div>
        <ModalInput />
        <ModalOutput />
      </>
    );
  }
}

const IoList = connect(mapStateToProps, mapDispatchToProps)(IoListComponent);

export default IoList;
